import { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import { formElementTypes } from "../../../../utils/Constant";
import dayjs, { Dayjs } from "dayjs";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import MultiInputPreview from "../../../admin/form-templates/form-builder/preview/multi-input";
import QuestionTable from "../../../admin/form-templates/form-builder/preview/table";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import QCPhotosList from "./qc-photos-list";
import { DragDropProvider, DragOverlay } from "@dnd-kit/react";
import { move } from "@dnd-kit/helpers";
import DragPhoto from "../inspection/drag-photo";

export default function ElementField({
  element,
  updateAnswer,
  canQCTakePhotos,
}: any) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [text, setText] = useState<string | null>(null);
  const [radioSelected, setRadioSelected] = useState<string>("");
  const [isRadioOtherSelected, setIsRadioOtherSelected] = useState(false);
  const [checkboxSelected, setCheckboxSelected] = useState<string[]>([]);
  const [isCheckboxOtherSelected, setIsCheckboxOtherSelected] = useState(false);
  const [date, setDate] = useState<Dayjs | null>(null);
  const [matrixData, setMatrixData] = useState<any[]>([]);
  const [multiInputData, setMultiInputData] = useState<any[]>([]);
  const [photos, setPhotos] = useState<any[]>([]);
  let tableData: any[] = [];

  useEffect(() => {
    if (element) {
      if (element?.type === formElementTypes.openEnd && element?.answers) {
        const answer = element?.answers?.qc?.answer
          ? element?.answers?.qc?.answer
          : element?.answers?.inspector?.answer || null;
        if (answer) {
          setText(answer);
          updateAnswer({ answer });
        }
      }
      if (element?.type === formElementTypes.singleSelect && element?.answers) {
        const answer = element?.answers?.qc?.answer
          ? element?.answers?.qc?.answer
          : element?.answers?.inspector?.answer || null;
        if (answer) {
          const exists = element?.settings?.answerOptions.includes(answer);
          if (exists) {
            setRadioSelected(answer);
          } else {
            setRadioSelected("Other");
            setIsRadioOtherSelected(true);
            setText(answer);
          }
          updateAnswer({ answer });
        }
      }
      if (element?.type === formElementTypes.multiSelect && element?.answers) {
        const answer = element?.answers?.qc?.answer
          ? element?.answers?.qc?.answer
          : element?.answers?.inspector?.answer || null;
        if (answer) {
          const answersArray = answer.split(",");
          const isNotOther = element?.settings?.answerOptions.includes(
            answersArray[answersArray.length - 1]
          );
          if (!isNotOther) {
            setIsCheckboxOtherSelected(true);
            setText(answersArray[answersArray.length - 1]);
          }
          setCheckboxSelected(answersArray);
          updateAnswer({ answer });
        }
      }
      if (element?.type === formElementTypes.singleDate && element?.answers) {
        const answer = element?.answers?.qc?.answer
          ? element?.answers?.qc?.answer
          : element?.answers?.inspector?.answer || null;
        if (answer) {
          setDate(dayjs(answer));
          updateAnswer({ answer });
        }
      }
      if (element?.type === formElementTypes.matrix) {
        const isQCAnswerAvailable =
          element?.childElements?.findIndex(
            (ele: any) => ele?.answer?.qc?.answer
          ) >= 0;
        const childElements: any[] = [];
        element?.childElements?.forEach((element: any) => {
          const data = {
            id: element.id,
            answer: isQCAnswerAvailable
              ? element?.answers?.inspector?.answer || null
              : element?.answers?.qc?.answer || null,
          };
          childElements.push(data);
        });
        setMatrixData(childElements);
        if (childElements?.length > 0) {
          updateAnswer({ childElements });
        }
      }
      if (element?.type === formElementTypes.photoRequest && element?.answers) {
        if (element?.answers?.qc?.photoAnswers) {
          setPhotos(element?.answers?.qc?.photoAnswers || []);
        } else {
          setPhotos(element?.answers?.inspector?.photoAnswers || []);
        }
      }
    }
  }, [element]); // eslint-disable-line

  useEffect(() => {
    if (text !== null) {
      const payload: any = {};
      if (
        element?.type === formElementTypes.openEnd ||
        element?.type === formElementTypes.singleSelect
      ) {
        payload.answer = text;
      } else if (element?.type === formElementTypes.multiSelect) {
        const answerArray = [...checkboxSelected];
        const isNotOther = element?.settings?.answerOptions.includes(
          answerArray[answerArray.length - 1]
        );
        if (text) {
          if (isNotOther) {
            // if other selected, then push the text in the last
            answerArray.push(text);
          } else {
            answerArray.splice(answerArray.length - 1, 1, text);
          }
        }
        payload.answer = answerArray.join(",");
      } else if (element?.type === formElementTypes.photoRequest) {
        payload.photoAnswer = { failureExplanation: text };
      }
      updateAnswer(payload);
    }
  }, [text, element]); // eslint-disable-line

  const onRadioSelectionChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = (event.target as HTMLInputElement).value;
    setRadioSelected(value);
    if (value !== "Other") {
      const payload = {
        answer: value,
      };
      updateAnswer(payload);
    }
  };

  const onCheckboxSelectionChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = (event.target as HTMLInputElement).value;
    let checkboxValues = [...checkboxSelected];
    if (value === "Other") {
      setIsCheckboxOtherSelected(event.target.checked);
      const isNotOther = element?.settings?.answerOptions.includes(
        checkboxValues[checkboxValues.length - 1]
      );
      if (!event.target.checked && !isNotOther) {
        checkboxValues.pop();
        setText(null);
        if (checkboxValues.length > 0) {
          const payload = {
            answer: checkboxValues.join(","),
          };
          updateAnswer(payload);
        } else {
          updateAnswer({ answer: "" });
        }
      }
    } else {
      if (checkboxSelected.length > 0) {
        if (event.target.checked) {
          checkboxValues.unshift(value);
          setCheckboxSelected(checkboxValues);
        } else {
          checkboxValues = checkboxValues.filter(
            (checkboxValue) => checkboxValue !== value
          );
          setCheckboxSelected(checkboxValues);
        }
      } else {
        checkboxValues.unshift(value);
        setCheckboxSelected(checkboxValues);
      }
      if (checkboxValues.length > 0) {
        const payload = {
          answer: checkboxValues.join(","),
        };
        updateAnswer(payload);
      } else {
        updateAnswer({ answer: "" });
      }
    }
  };

  const onDateChange = (date: Dayjs | null) => {
    setDate(date);
    if (date) {
      const formattedDate = dayjs(date).format("YYYY-MM-DD");
      if (dayjs(formattedDate).isValid()) {
        const payload = {
          answer: formattedDate,
        };
        updateAnswer(payload);
      }
    }
  };

  const onMatrixRadioSelectionChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    childElement: any
  ) => {
    const value = (event.target as HTMLInputElement).value;
    const childElements = [...matrixData];
    if (childElements.length > 0) {
      const index = childElements.findIndex(
        (element) => element.id === childElement.id
      );
      if (index >= 0) {
        childElements[index].answer = value;
      } else {
        const data = {
          id: childElement.id,
          answer: value,
        };
        childElements.push(data);
      }
    } else {
      const data = {
        id: childElement.id,
        answer: value,
      };
      childElements.push(data);
    }
    setMatrixData(childElements);
    const payload = {
      childElements,
    };
    updateAnswer(payload);
  };

  const checkMatrixSelection = (childElement: any, answer: string) => {
    const index = matrixData.findIndex(
      (element: any) => element.id === childElement.id
    );
    return matrixData[index]?.answer === answer;
  };

  const onMultiInputValueChange = (inputValue: any) => {
    const childElements = [...multiInputData];
    if (childElements.length > 0) {
      const index = childElements.findIndex(
        (element) => element.id === inputValue?.id
      );
      if (index >= 0) {
        childElements[index].answer = inputValue?.answer;
      } else {
        const data = {
          id: inputValue.id,
          answer: inputValue.answer,
        };
        childElements.push(data);
      }
    } else {
      const data = {
        id: inputValue.id,
        answer: inputValue.answer,
      };
      childElements.push(data);
    }
    setMultiInputData(childElements);
    const payload = {
      childElements,
    };
    updateAnswer(payload);
  };

  const onTableValueChange = (inputValue: any) => {
    const tableChildElements = [...tableData];
    if (tableChildElements.length > 0) {
      const index = tableChildElements.findIndex(
        (element: any) => element.id === inputValue?.id
      );
      if (index >= 0) {
        tableChildElements[index].answer = inputValue?.answer;
      } else {
        const data = {
          id: inputValue.id,
          answer: inputValue.answer,
        };
        tableChildElements.push(data);
      }
    } else {
      const data = {
        id: inputValue.id,
        answer: inputValue.answer,
      };
      tableChildElements.push(data);
    }
    tableData = [...tableChildElements];
    const payload = {
      childElements: tableChildElements,
    };
    updateAnswer(payload);
  };

  const handleImageSelect = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event?.target?.files?.length === 0) {
      return;
    }
    const files = event.target.files || [];
    const arrayFiles = Array.from(files);
    const finalFiles: any[] = [];
    arrayFiles.forEach((file) => {
      const fileObj = {
        file: file,
        description: "",
      };
      finalFiles.push(fileObj);
    });
    setPhotos((prev) => [...prev, ...finalFiles]);
  };

  const onPhotoDelete = (deleteIndex: number) => {
    setPhotos((prev) =>
      prev.map((item, i) =>
        i === deleteIndex ? { ...item, isDeleted: true } : item
      )
    );
  };

  const onPhotoDeleteUndo = (deleteIndex: number) => {
    setPhotos((prev) =>
      prev.map((item, i) =>
        i === deleteIndex ? { ...item, isDeleted: false } : item
      )
    );
  };

  const onPhotoDescriptionUpdate = (index: number, description: string) => {
    setPhotos((prev) =>
      prev.map((item, i) =>
        i === index ? { ...item, description: description } : item
      )
    );
  };

  useEffect(() => {
    if (photos && element?.type === formElementTypes.photoRequest) {
      updateAnswer(photos);
    }
  }, [photos]); // eslint-disable-line

  return (
    <>
      {element?.type === formElementTypes.openEnd && (
        <TextField
          sx={{ mt: 1 }}
          fullWidth
          multiline
          minRows={3}
          placeholder="Value"
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
      )}
      {element?.type === formElementTypes.singleSelect && (
        <FormControl sx={{ width: "100%" }}>
          <RadioGroup
            name="radio-buttons-group"
            value={radioSelected}
            onChange={onRadioSelectionChange}
          >
            {element?.settings?.answerOptions.map((answer: string) => (
              <FormControlLabel
                key={answer}
                value={answer}
                control={
                  <Radio
                    checked={answer === radioSelected}
                    onChange={(event) => setIsRadioOtherSelected(false)}
                  />
                }
                label={answer}
              />
            ))}
            {element?.settings?.includeOtherOption && (
              <FormControlLabel
                key="other"
                value="Other"
                control={
                  <Radio
                    checked={"Other" === radioSelected}
                    onChange={(event) =>
                      setIsRadioOtherSelected(event.target.checked)
                    }
                  />
                }
                label="Other"
              />
            )}
            {isRadioOtherSelected && (
              <TextField
                sx={{ mt: 1 }}
                fullWidth
                placeholder="Value"
                value={text}
                onChange={(e) => setText(e.target.value)}
              />
            )}
          </RadioGroup>
        </FormControl>
      )}
      {element?.type === formElementTypes.multiSelect && (
        <FormControl sx={{ width: "100%" }}>
          <FormGroup>
            {element?.settings?.answerOptions.map((answer: string) => (
              <FormControlLabel
                key={answer}
                value={answer}
                control={
                  <Checkbox
                    checked={checkboxSelected.includes(answer)}
                    onChange={onCheckboxSelectionChange}
                  />
                }
                label={answer}
              />
            ))}
            {element?.settings?.includeOtherOption && (
              <FormControlLabel
                key="other"
                value="Other"
                control={
                  <Checkbox
                    checked={isCheckboxOtherSelected}
                    onChange={onCheckboxSelectionChange}
                  />
                }
                label="Other"
              />
            )}
            {isCheckboxOtherSelected && (
              <TextField
                sx={{ mt: 1 }}
                fullWidth
                placeholder="Value"
                value={text}
                onChange={(e) => setText(e.target.value)}
              />
            )}
          </FormGroup>
        </FormControl>
      )}
      {element?.type === formElementTypes.matrix && (
        <Box sx={{ borderLeft: "1px solid #C4C4C4", width: "100%" }}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography className="question-matrix-header-text"></Typography>
            {element?.settings?.answerOptions?.map((option: string) => (
              <Typography key={option} className="question-matrix-header-text">
                {option}
              </Typography>
            ))}
          </Box>
          {element?.childElements?.map((question: any, index: number) => (
            <Box
              key={index}
              sx={{ display: "flex", alignItems: "center", width: "100%" }}
            >
              <Box
                className="question-matrix-subquestions-layout"
                sx={{
                  width: `calc(100%/${
                    element?.settings?.answerOptions.length + 1
                  } - 20px)`,
                }}
              >
                <Typography className="question-matrix-subquestion-txt">
                  {question.title}
                </Typography>
                <Typography
                  className="question-matrix-subquestion-defined-name"
                  noWrap
                >
                  #{question.definedName}
                </Typography>
              </Box>
              <RadioGroup
                name="matrix-radio-buttons-group"
                sx={{
                  display: "flex",
                  height: 67,
                  width: `calc(100% - 100%/${
                    element?.settings?.answerOptions.length + 1
                  })`,
                }}
                onChange={(e) => onMatrixRadioSelectionChange(e, question)}
              >
                {element?.settings?.answerOptions.map((answer: string) => (
                  <FormControlLabel
                    className="question-matrix-subquestion-radio"
                    sx={{ flex: 1, mr: 0 }}
                    key={answer}
                    value={answer}
                    control={
                      <Radio checked={checkMatrixSelection(question, answer)} />
                    }
                    label=""
                  />
                ))}
              </RadioGroup>
            </Box>
          ))}
        </Box>
      )}

      {element?.type === formElementTypes.table && (
        <QuestionTable
          element={element}
          inputValueChange={onTableValueChange}
          isQC={true}
        />
      )}

      {element?.type === formElementTypes.multiInput && (
        <Box sx={{ width: "100%" }}>
          <MultiInputPreview
            element={element}
            inputValueChange={onMultiInputValueChange}
            isQC={true}
          />
        </Box>
      )}

      {element?.type === formElementTypes.singleDate && (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            sx={{ width: 300, mt: 1 }}
            format="LL"
            value={date}
            onChange={onDateChange}
          />
        </LocalizationProvider>
      )}

      {element?.type === formElementTypes.photoRequest && (
        <Box sx={{ width: "calc(100% - 100px)" }}>
          <DragDropProvider
            onDragEnd={(event) => {
              setPhotos((items) => move(items, event));
            }}
          >
            <Box>
              {photos.length > 0 &&
                photos?.map((photo: any, index: number) => (
                  <QCPhotosList
                    key={photo?.key}
                    id={photo?.key}
                    photoData={photo}
                    index={index}
                    onDescriptionUpdate={onPhotoDescriptionUpdate}
                    onDelete={onPhotoDelete}
                    onDeleteUndo={onPhotoDeleteUndo}
                  />
                ))}
            </Box>
            <DragOverlay>
              {(source) => (
                <DragPhoto
                  photoData={photos.find((photo) => photo?.key === source.id)}
                />
              )}
            </DragOverlay>
          </DragDropProvider>
          <>
            <Button
              sx={{ mt: 2 }}
              variant="text"
              startIcon={<AddOutlinedIcon />}
              disabled={canQCTakePhotos}
              onClick={() => {
                fileInputRef.current?.click();
              }}
            >
              Add Photos
            </Button>
            <input
              hidden
              multiple
              accept="image/*"
              type="file"
              ref={fileInputRef}
              onChange={handleImageSelect}
              onClick={(event) => {
                // same file selection issue
                (event.target as HTMLInputElement).value = "";
              }}
            />
          </>
        </Box>
      )}
    </>
  );
}
